/** STANDARD rendering path for a composition channel: pick the Standard_Template
 *  layout and show the full provenance of what a delivery will compose —
 *  layout, block refs (links to the composition panel), theme and language.
 */
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import TranslateIcon from '@mui/icons-material/Translate';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import type { NotificationDeliveryMethod, NotificationDetail } from '@/api/notifications';
import { listStandardTemplates } from '@/api/notifications';
import { FieldHelp } from '@/components/FieldHelp';
import { RemoteAutocomplete } from '@/components/RemoteAutocomplete/RemoteAutocomplete';
import { ValueActions } from '@/components/ValueActions/ValueActions';

import { NOTIFICATION_HELP } from '../fieldHelp';
import { languagePolicyActions, themeActions } from '../notificationLinks';
import { COMPOSITION_PANEL_ID } from '../NotificationCompositionPanel';
import type { ChannelSpec } from './channelFields';

interface TemplateOption {
  id: number;
  name: string;
  version: number;
  template_language: string;
  is_system?: boolean;
}

interface Props {
  dm: NotificationDeliveryMethod;
  spec: ChannelSpec;
  detail: NotificationDetail;
  /** Live count from the draft (the composition panel may have unsaved rows). */
  blockRefCount: number;
  canManage: boolean;
  onTemplateChange: (templateId: number | null) => void;
}

export function StandardPathPanel({
  dm,
  spec,
  detail,
  blockRefCount,
  canManage,
  onTemplateChange,
}: Props) {
  const templateField = spec.templateField;
  const templateId = templateField != null ? ((dm[templateField] as number | null) ?? null) : null;
  const refBrief =
    templateId != null ? detail.refs.standard_templates[String(templateId)] : undefined;

  const [selected, setSelected] = useState<TemplateOption | null>(refBrief ?? null);
  useEffect(() => {
    setSelected((prev) => {
      if ((prev?.id ?? null) === templateId) return prev;
      if (templateId == null) return null;
      const brief = detail.refs.standard_templates[String(templateId)];
      return (
        brief ?? {
          id: templateId,
          name: `template #${templateId}`,
          version: 0,
          template_language: '?',
        }
      );
    });
  }, [templateId, detail.refs.standard_templates]);

  const fetchTemplates = useCallback(
    (q: string): Promise<TemplateOption[]> =>
      listStandardTemplates({
        method: spec.method,
        tour_operator_id: detail.notification.tour_operator ?? 'system',
        ...(q ? { search: q } : {}),
      }).then((r) => r.results),
    [spec.method, detail.notification.tour_operator],
  );

  const themeCodename =
    dm.theme != null ? (detail.refs.themes[String(dm.theme)]?.codename ?? `#${dm.theme}`) : null;

  const scrollToComposition = () => {
    document
      .getElementById(COMPOSITION_PANEL_ID)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Stack spacing={1.5}>
      <Box sx={{ maxWidth: 480, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ flexGrow: 1 }}>
          <RemoteAutocomplete<TemplateOption>
            size="small"
            label="Standard template (layout)"
            placeholder="Channel default layout"
            queryFn={fetchTemplates}
            getOptionLabel={(t) =>
              `${t.name} v${t.version} (${t.template_language})${t.is_system ? ' · system' : ''}`
            }
            getOptionKey={(t) => t.id}
            value={selected}
            onChange={(v) => {
              if (!canManage) return;
              const opt = v as TemplateOption | null;
              setSelected(opt);
              onTemplateChange(opt?.id ?? null);
            }}
            fetchOnEmpty
          />
        </Box>
        <FieldHelp help={NOTIFICATION_HELP.standardTemplate} />
      </Box>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          variant="outlined"
          icon={<ArticleOutlinedIcon />}
          color={selected ? 'default' : 'warning'}
          label={
            selected
              ? `Layout: ${selected.name} v${selected.version} · ${selected.template_language}`
              : 'Layout: channel default'
          }
        />
        <Chip
          size="small"
          variant="outlined"
          icon={<ExtensionOutlinedIcon />}
          label={`Blocks: ${blockRefCount} ref${blockRefCount === 1 ? '' : 's'}`}
          onClick={scrollToComposition}
        />
        {dm.theme != null ? (
          <ValueActions chip actions={themeActions({ id: dm.theme, codename: themeCodename })}>
            <Chip
              size="small"
              variant="outlined"
              icon={<PaletteOutlinedIcon />}
              label={`Theme: ${themeCodename}`}
            />
          </ValueActions>
        ) : (
          <Chip
            size="small"
            variant="outlined"
            icon={<PaletteOutlinedIcon />}
            label="Theme: operator default"
          />
        )}
        {detail.refs.language_policy ? (
          <ValueActions chip actions={languagePolicyActions(detail.refs.language_policy)}>
            <Chip
              size="small"
              variant="outlined"
              icon={<TranslateIcon />}
              label={`Language: ${detail.refs.language_policy.codename}`}
            />
          </ValueActions>
        ) : (
          <Chip
            size="small"
            variant="outlined"
            icon={<TranslateIcon />}
            label="Language: standard resolution"
          />
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary">
        Standard rendering composes the referenced blocks into the selected layout — the per-channel
        custom sources below are ignored while this path is active.
      </Typography>
    </Stack>
  );
}
